import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';


interface TMRoleResponse {
  has_users: boolean;
}

@Injectable()
export class AgencyService {
  constructor(private http: HttpClient) {
  }

  show(id) {
    return this.http.get<any>(`${environment.apiUrl}/agency/getById/${id}`);
  }

  inviteMember(invitation) {
    return this.http.post<any>(`${environment.apiUrl}/agency/invite-member`, invitation);
  }

  inviteClientMembers(invitation) {
    return this.http.post<any>(`${environment.apiUrl}/agency/invite-client-members`, invitation);
  }

  resendInvitation(email) {
    return this.http.post<any>(`${environment.apiUrl}/agency/resend-invite`, email);
  }

  public handleError = (error: Response) => {
    return throwError(error)
  }

  store(agency) {
    return this.http.post<any>(`${environment.apiUrl}/agency/store`, agency);
  }

  updatePermissions(permissions) {
    return this.http.post<any>(`${environment.apiUrl}/agency/update-permissions`, permissions);
  }

  deleteInvitation(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/delete-invitation`, param);
  }

  reinviteUser(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/re-invite-user`, param);
  }

  getAgencies() {
    return this.http.get<any>(`${environment.apiUrl}/agency/index`);
  }

  getAgencyName(id) {
    return this.http.get<any>(`${environment.apiUrl}/agency/name/${id}`);
  }

  getSuperadminAgencies() {
    return this.http.get<any>(`${environment.apiUrl}/superadmin/agency/get-agencies`);
  }

  getWhitelabels() {
    return this.http.get<any>(`${environment.apiUrl}/superadmin/agency/get-whitelabels`);
  }

  getAgencyBranches() {
    return this.http.get<any>(`${environment.apiUrl}/agency/branches`);
  }

  exportXml() {
    return this.http.get<any>(`${environment.apiUrl}/exportXML`);
  }

  exportTxt() {
    return this.http.get<any>(`${environment.apiUrl}/export-txt`);
  }

  checkIfOpenPayroll() {
    return this.http.get<any>(`${environment.apiUrl}/check-if-open-payroll`);
  }

  exportXLSX() {
    return this.http.get<any>(`${environment.apiUrl}/export-xlsx`);
  }
  
  downloadXLSX(param) {
    return this.http.post<any>(`${environment.apiUrl}/download-xlsx`, param);
  }

  checkPayrollMonths(param) {
    return this.http.post<any>(`${environment.apiUrl}/check-payroll-months`, param);
  }

  deleteXLSXFileFromServer(param) {
    return this.http.post<any>(`${environment.apiUrl}/delete-xlsx-from-server`, param);
  }

  deletePayroll(id) {
    return this.http.delete<any>(`${environment.apiUrl}/delete-payroll/${id}`);
  }

  isAbacusExportEnabled() {
    return this.http.get<any>(`${environment.apiUrl}/agency/abacus-export/is-enabled`);
  }

  isMirusExportEnabled() {
    return this.http.get<any>(`${environment.apiUrl}/agency/mirus-export/is-enabled`);
  }

  toggleMirusExport(agencyId: number, data: {is_enabled: boolean}) {
    return this.http.post<any>(`${environment.apiUrl}/agency/${agencyId}/mirus-export/toggle`, data);
  }

  payslipPdfForAllEmployeesInOneMonth(param) {
      return this.http.post<any>(`${environment.apiUrl}/all-employees-pdf`, param);
  }

  getXMLs() {
    return this.http.get<any>(`${environment.apiUrl}/get-xmls`);
  }

  updateTemplate(id, template) {
    return this.http.put<any>(`${environment.apiUrl}/agency/template/${id}`, template);
  }

  loadTemplates(type, clientId: number | null = null) {
    // JS-1871: when the caller passes clientId (e.g. the client profile Edit Templates
    // tab mounting EditTemplatesComponent with [clientId]=profile.id), the backend scopes
    // the list to that client's templates (IDOR-checked in TemplateController::loadTemplates).
    const scope = clientId ? `&client_id=${clientId}` : '';
    return this.http.get<any>(`${environment.apiUrl}/agency/template?type=${type}${scope}`);
  }

  getProjectTypes(clientId: number | null = null) {
    // JS-1871: pass client_id when the caller is on a self-service surface so the
    // dropdown returns only that client's project types.
    const q = clientId ? `?client_id=${clientId}` : '';
    return this.http.get<any>(`${environment.apiUrl}/project/project-types${q}`);
  }

  deleteTemplate(id) {
    return this.http.delete<any>(`${environment.apiUrl}/agency/template/${id}`);
  }

  removeClothingTemplateImage(id, param) {
    return this.http.put<any>(`${environment.apiUrl}/agency/clothing-template/${id}`, param);
  }

  deleteProjectType(id, clientId: number | null = null) {
    // JS-1871: forward client_id so the backend's canTouchProjectType accepts an agency
    // user deleting a client-scoped project type from the client profile Edit Templates tab.
    const q = clientId ? `?client_id=${clientId}` : '';
    return this.http.delete<any>(`${environment.apiUrl}/project/project-type/${id}${q}`);
  }

  editProjectType(id, projectType) {
    return this.http.put(`${environment.apiUrl}/project/project-type/${id}`, projectType);
  }

  getProfile(id: any) {
    return this.http.get<any>(`${environment.apiUrl}/agency/profile/${id}`);
  }

  getProjectReport(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/project-report`, param);
  }

  getWords(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/get-words`, param);
  }

  updateTranslatation(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/updateTranslatation`, param);
  }

  saveTranslatation(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/saveTranslatation`, param);
  }

  getNotTranslated(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/getNotTranslated`, param);
  }

  getTranslated(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/getTranslated`, param);
  }

  addCredits(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/add-credits`, param);
  }

  getAgencyCredits(agencyId) {
      return this.http.get<any>(`${environment.apiUrl}/agency/get-credits/${agencyId}`);
  }

  getAgencyTotalCredits(agencyId) {
      return this.http.get<any>(`${environment.apiUrl}/agency/get-total-credits/${agencyId}`);
  }

  generateXLSX(param) {
      return this.http.post<any>(`${environment.apiUrl}/agency/generate-xlsx`, param);
  }

  updateTermsAndConditions(param) {
      return this.http.put(`${environment.apiUrl}/agency/update-tos`, param);
  }

  updateClockTolerance(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-clock-tolerance`, param);
  }

  upladteEarlyClockTolerance(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-early-clock-tolerance`, param);
  }
  
  updateLateClockInTolerance(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-late-clock-in-tolerance`, param);
  }

  updateLateClockInJustificationTolerance(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-late-clock-in-justification-tolerance`, param);
  }
  
  updateWorkPermissionExpiryDelayTolerance(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-work-permission-expiry-delay-tolerance`, param);
  }
  
  updateShiftDelayTimes(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-shift-delay-times`, param);
  }
  
  updateFTE(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-fte`, param);
  }
  
  updateMandays(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-mandays`, param);
  }

  updateEmployeeListNumbers(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-employee-list-numbers`, param);
  }

  updateEmailInfo(id, param) {
      return this.http.put<any>(`${environment.apiUrl}/agency/update-email-info/${id}`, param);
  }

  removeTermsAndConditionsFile(id) {
      return this.http.delete(`${environment.apiUrl}/agency/tos-file/${id}`);
  }

  removeLogo(id) {
      return this.http.delete(`${environment.apiUrl}/agency/logo/${id}`);
  }

  searchEmployees(param) {
      return this.http.get<any>(`${environment.apiUrl}/employee/settings?search=${param}`)
  }

  selectedEmployee(id) {
      return this.http.get<any>(`${environment.apiUrl}/employee/settings/${id}`)
  }

  updateSelectedEmployee(param) {
      return this.http.put<any>(`${environment.apiUrl}/employee/settings`, param)
  }

  addPools(param) {
      return this.http.post<any>(`${environment.apiUrl}/employee/add-pools`, param);
  }

  removePools(param) {
      return this.http.post<any>(`${environment.apiUrl}/employee/remove-pools`, param);
  }

  storeBulkAPC(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/active-permanent-contract/store-contracts`, param);
  }

  updateNotifyEmail(param) {
      return this.http.put(`${environment.apiUrl}/agency/update-notify-email`, param);
  }

  updateMissionAcceptEmail(param) {
      return this.http.put(`${environment.apiUrl}/agency/update-mission-accept-email`, param);
  }

  updateMissionCancelEmail(param) {
      return this.http.put(`${environment.apiUrl}/agency/update-mission-cancel-email`, param);
  }

  updateJobApplicantEmail(param) {
      return this.http.put(`${environment.apiUrl}/agency/update-job-applicant-email`, param);
  }

  updateSelfBookingEmail(param) {
      return this.http.put(`${environment.apiUrl}/agency/update-self-booking-email`, param);
  }
  
  updateLocationAlertDistance(param, clientId: number | null = null) {
      // JS-1936: when the caller passes clientId (ClientSettingsPanel on client profile /
      // client settings page), the backend writes to the client's override column instead
      // of the agency default. Without clientId, this hits the agency-level column — current
      // agency-profile behaviour.
      const body = clientId ? { ...param, client_id: clientId } : param;
      return this.http.put(`${environment.apiUrl}/agency/update-location-alert-distance`, body);
  }

  updateTwilioSettings(param) {
      return this.http.put(`${environment.apiUrl}/agency/update-twilio-settings`, param);
  }

  updateVisibility(param) {
      return this.http.put<any>(`${environment.apiUrl}/agency/visibility`, param);
  }

  getAgencyFields(agencyId) {
      return this.http.get<any>(`${environment.apiUrl}/agency/fields/${agencyId}`);
  }

  updateAgencyFields(agencyId, param) {
      return this.http.put<any>(`${environment.apiUrl}/agency/fields/${agencyId}`, param);
  }

  storeAgencyContact(param) {
      return this.http.post<any>(`${environment.apiUrl}/agency/contact`, param);
  }

  storeHrManagers(param) {
      return this.http.post<any>(`${environment.apiUrl}/agency/hr-managers`, param);
  }

  getAgencyContacts(agencyId) {
      return this.http.get<any>(`${environment.apiUrl}/agency/contacts/${agencyId}`);
  }

  deleteAgencyContact(id) {
      return this.http.delete<any>(`${environment.apiUrl}/agency/contact/${id}`);
  }

  updateUserRole(param) {
      return this.http.put<any>(`${environment.apiUrl}/user/role`, param);
  }

  storeBreak(param, clientId: number | null = null) {
      // JS-1936: when the caller passes clientId (ClientSettingsPanel on client profile /
      // client settings page), the backend saves the row pinned to that client's bucket.
      // Without clientId, the row is agency-wide (NULL) — current agency profile behaviour.
      const body = clientId ? { ...param, client_id: clientId } : param;
      return this.http.post<any>(`${environment.apiUrl}/agency/break`, body);
  }

  getBreaks(clientId: number | null = null) {
      // JS-1936: client-scoped list when clientId is supplied; agency-defaults list otherwise.
      // Backend's AgencyBreakController never mixes the two sets.
      const q = clientId ? `?client_id=${clientId}` : '';
      return this.http.get<any>(`${environment.apiUrl}/agency/breaks${q}`);
  }

  removeBreak(id) {
      return this.http.delete<any>(`${environment.apiUrl}/agency/break/${id}`);
  }

  deleteEmployee(id) {
    return this.http.delete<any>(`${environment.apiUrl}/superadmin/employee/${id}`);
  }

  deleteRoleFile(id, body) {
    return this.http.delete<any>(`${environment.apiUrl}/agency/template/${id}/role-file`, body);
  }

  getAgencyPermanentEmployeeGroups(agencyId) {
    return this.http.get<any>(`${environment.apiUrl}/agency/permanent-employee-group/${agencyId}`);
  }

  storePermanentEmployeeGroup(body) {
    return this.http.post<any>(`${environment.apiUrl}/agency/permanent-employee-group`, body);
  }

  updatePermanentEmployeeGroup(id, body) {
    return this.http.put<any>(`${environment.apiUrl}/agency/permanent-employee-group/${id}`, body);
  }

  deletePermanentEmployeeGroup(id) {
    return this.http.delete<any>(`${environment.apiUrl}/agency/permanent-employee-group/${id}`);
  }

  getAgencyPermanentEmployees() {
    return this.http.get<any>(`${environment.apiUrl}/agency/permanent-employee-group/employees`);
  }

  addOrRemoveEmployeeFromGroup(body) {
    return this.http.post<any>(`${environment.apiUrl}/agency/permanent-employee-group/employees`, body);
  }

  getAbsentCategories(agencyId) {
    return this.http.get<any>(`${environment.apiUrl}/agency/absence-categories/${agencyId}`);
  }

  storeAbsentCategory(body) {
    return this.http.post<any>(`${environment.apiUrl}/agency/absence-categories`, body);
  }

  updateAbsentCategory(id, body) {
    return this.http.put<any>(`${environment.apiUrl}/agency/absence-categories/${id}`, body);
  }

  deleteAbsentCategory(id) {
    return this.http.delete<any>(`${environment.apiUrl}/agency/absence-categories/${id}`);
  }

  getPublicHolidays() {
    return this.http.get<any>(`${environment.apiUrl}/agency/public-holiday`);
  }

  storePublicHolidays(body) {
    return this.http.post<any>(`${environment.apiUrl}/agency/public-holiday`, body);
  }

  importHolidaysFromXls(body) {
    return this.http.post<any>(`${environment.apiUrl}/agency/public-holiday/import`, body);
  }

  updatePublicHolidays(id, body) {
    return this.http.put<any>(`${environment.apiUrl}/agency/public-holiday/${id}`, body);
  }

  deletePublicHolidays(id) {
    return this.http.delete<any>(`${environment.apiUrl}/agency/public-holiday/${id}`);
  }

  getMandatoryFields() {
    return this.http.get<any>(`${environment.apiUrl}/agency/mandatory-field`);
  }

  storeMandatoryField(body) {
    return this.http.post<any>(`${environment.apiUrl}/agency/mandatory-field`, body);
  }

  deleteMandatoryField(id) {
    return this.http.delete<any>(`${environment.apiUrl}/agency/mandatory-field/${id}`);
  }

  getEditFields(agencyId?: number) {
    const params = agencyId ? `?agency_id=${agencyId}` : '';
    return this.http.get<any>(`${environment.apiUrl}/agency/edit-field${params}`);
  }

  storeEditField(body, agencyId?: number) {
    const payload = agencyId ? { ...body, agency_id: agencyId } : body;
    return this.http.post<any>(`${environment.apiUrl}/agency/edit-field`, payload);
  }

  deleteEditField(id, agencyId?: number) {
    const params = agencyId ? `?agency_id=${agencyId}` : '';
    return this.http.delete<any>(`${environment.apiUrl}/agency/edit-field/${id}${params}`);
  }

  getSedCards(){
    return this.http.get<any>(`${environment.apiUrl}/agency/sed-card-templates`);
  }

  postSedCards(model){
    return this.http.post<any>(`${environment.apiUrl}/agency/sed-card-templates`, model);
  }

  setLowCreditsEmail(data) {
    return this.http.put<any>(`${environment.apiUrl}/agency/low-credits-email`, data);
  }

  // JS-1936 (2026-10-26, Bleona): updateClientEmailInfo removed — the per-client SMTP
  // surface was deleted; no caller remains.

  // JS-1936 (2026-10-26, Bleona): updateClientTwilioSettings removed — the per-client Twilio
  // surface was deleted; no caller remains.

  // JS-1936: per-client "Email notifications" update endpoint. Writes all four recipient
  // lists in a single call — the backend trigger sites resolve client-first with agency
  // fallback at send time.
  updateClientNotificationEmails(clientId: number, param) {
    return this.http.put<any>(`${environment.apiUrl}/client/update-notification-emails/${clientId}`, param);
  }

  setAssignmentNotifications(data, clientId: number | null = null) {
    // JS-1936: when clientId is supplied the backend writes to that client's toggle;
    // otherwise it falls back to the agency-level column (original behaviour).
    const body = clientId ? { ...data, client_id: clientId } : data;
    return this.http.put<any>(`${environment.apiUrl}/agency/assignment-notifications`, body);
  }

  nohWorkingHours(working_hours){
    return this.http.post<any>(`${environment.apiUrl}/agency/noh-working-hours`,{working_hours});
  }

  nohStepOne(project_type_id,agency_contact_id){
    return this.http.post<any>(`${environment.apiUrl}/agency/noh-step-one`,{project_type_id,agency_contact_id});
  }

  nohBuffer(buffer_time){
    return this.http.post<any>(`${environment.apiUrl}/agency/noh-buffer`,{buffer_time});
  }

  autoAssignmentAfterXMin(auto_assginment_after_x_mins){
    return this.http.post<any>(`${environment.apiUrl}/agency/auto-assginment-after-x-mins`,{auto_assginment_after_x_mins});
  }

  nohNotificationTemplate(email_template_id){
    return this.http.post<any>(`${environment.apiUrl}/agency/noh-notification-template`,{email_template_id});
  }

  getNohSwitch(){
    return this.http.get<any>(`${environment.apiUrl}/agency/noh-switch`);
  }

  autoAcceptMissionRequests(auto_accept_mission_requests){
    return this.http.post<any>(`${environment.apiUrl}/agency/auto-accept-mission-requests`, {auto_accept_mission_requests});
  }

  getSedCardTemplates(){
    return this.http.get<any>(`${environment.apiUrl}/agency/sed-card-templates`)
  }

  getSedCardTemplateById(id){
    return this.http.get<any>(`${environment.apiUrl}/agency/sed-card-templates/${id}`)
  }

  postSedCardTemplates(name,body,agency_id){
    return this.http.post<any>(`${environment.apiUrl}/agency/sed-card-templates`,{name,body,agency_id})
  }

  editSedCardTemplates(id,name,body,agency_id){
    return this.http.put<any>(`${environment.apiUrl}/agency/sed-card-templates/${id}`,{name,body,agency_id})
  }

  deleteSedCardTemplates(id){
    return this.http.delete<any>(`${environment.apiUrl}/agency/sed-card-templates/${id}`)
  }

  upladteEarlyJustificationTolerance(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-early-justification-tolerance`, param);
  }
  
  updateJustificationTolerance(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-late-justification-tolerance`, param);
  }
  
  isTMRoleSelectedOnProject(param): Observable<TMRoleResponse> {
    return this.http.post<TMRoleResponse>(`${environment.apiUrl}/agency/tm-role-selected-on-project`, param);
  }
  
  getRoleClients(id) {
    return this.http.get<any>(`${environment.apiUrl}/agency/template/get-role-clients/${id}`);
  }

  addClientToRoleTemplate(param) {
    return this.http.post(`${environment.apiUrl}/agency/template/add-client-to-role-template`, param);
  }

  removeClientFromRoleTemplate(param) {
    return this.http.post(`${environment.apiUrl}/agency/template/remove-client-from-role-template`, param);
  }

  // JS-1795: agency-wide tariff groups list (agency edit-templates page)
  getAgencyTariffGroups() {
    return this.http.get<any>(`${environment.apiUrl}/agency/template/agency-tariff-groups`);
  }

  // JS-1795 (template refactor): tariff-group templates on the agency page + pickList.
  getTariffGroupTemplates() {
    return this.http.get<any>(`${environment.apiUrl}/agency/template?type=tariff_group`);
  }

  saveTariffGroupTemplate(payload) {
    return this.http.post<any>(`${environment.apiUrl}/agency/template`, { ...payload, type: 'tariff_group' });
  }

  updateTariffGroupTemplate(id, payload) {
    return this.http.put<any>(`${environment.apiUrl}/agency/template/${id}`, { ...payload, type: 'tariff_group' });
  }

  deleteTariffGroupTemplate(id) {
    return this.http.delete<any>(`${environment.apiUrl}/agency/template/${id}`);
  }

  getTariffGroupTemplateClients(id) {
    return this.http.get<any>(`${environment.apiUrl}/agency/template/get-tariff-group-template-clients/${id}`);
  }

  addClientToTariffGroupTemplate(param) {
    return this.http.post(`${environment.apiUrl}/agency/template/add-client-to-tariff-group-template`, param);
  }

  removeClientFromTariffGroupTemplate(param) {
    return this.http.post(`${environment.apiUrl}/agency/template/remove-client-from-tariff-group-template`, param);
  }

  // JS-1795 (template refactor): client-profile roles tab — list templates by client id.
  getTariffGroupTemplatesByClient(clientId, params: any = {}) {
    return this.http.get<any>(`${environment.apiUrl}/agency/template/tariff-group-templates-by-client/${clientId}`, { params });
  }

  // JS-1795 (5B): confirm-and-overwrite helpers for tariff-group templates.
  checkIfTariffTemplateExists(name: string) {
    return this.http.post<any>(`${environment.apiUrl}/agency/template-exists`, { name, type: 'tariff_group' });
  }

  saveExistingTariffTemplate(payload) {
    return this.http.post<any>(`${environment.apiUrl}/agency/existing-template`, { ...payload, type: 'tariff_group' });
  }

  // JS-1795: agency clients + their departments, for the Add Tariff Group modal
  getAgencyClientsWithDepartments() {
    return this.http.get<any>(`${environment.apiUrl}/agency/template/agency-clients-with-departments`);
  }

  // JS-1795: tariff group ↔ client pickList
  getTariffGroupClients(id) {
    return this.http.get<any>(`${environment.apiUrl}/agency/template/get-tariff-group-clients/${id}`);
  }

  addClientToTariffGroup(param) {
    return this.http.post(`${environment.apiUrl}/agency/template/add-client-to-tariff-group`, param);
  }

  removeClientFromTariffGroup(param) {
    return this.http.post(`${environment.apiUrl}/agency/template/remove-client-from-tariff-group`, param);
  }
  
  updateWorkPermitExpiryNotifyEmail(param) {
    return this.http.put(`${environment.apiUrl}/agency/work-permit-expiry-notify-email`, param);
  }
  
  updateLowCreditsNotifyEmail(param) {
    return this.http.put(`${environment.apiUrl}/agency/update-low-credits-notify-email`, param);
  }

  getClothingRequirements() {
    return this.http.get(`${environment.apiUrl}/agency/agency-clothing-requirements`);
  }

  saveNewClothingRequirement(param) {
    return this.http.post(`${environment.apiUrl}/agency/agency-clothing-requirements`, param);
  }

  updateClothingRequirement(id, param) {
    return this.http.put(`${environment.apiUrl}/agency/agency-clothing-requirement/${id}`, param);
  }

  deleteClothingRequirement(id) {
    return this.http.delete(`${environment.apiUrl}/agency/agency-clothing-requirement/${id}`,);
  }

  getMaxWorkingHoursPerWeek() {
    return this.http.get(`${environment.apiUrl}/agency/max-working-hours-per-week`);
  }

  saveMaxWorkingHoursPerWeek(param) {
    return this.http.post(`${environment.apiUrl}/agency/max-working-hours-per-week`, param);
  }

  updateMaxWorkingHoursPerWeek(id, param) {
    return this.http.put(`${environment.apiUrl}/agency/max-working-hours-per-week/${id}`, param);
  }

  deleteMaxWorkingHoursPerWeek(id) {
    return this.http.delete(`${environment.apiUrl}/agency/max-working-hours-per-week/${id}`,);
  }

  // JS-1669 - configurable working-time / compliance business rules matrix
  getBusinessRules() {
    return this.http.get<any>(`${environment.apiUrl}/agency/business-rules`);
  }

  saveBusinessRules(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/business-rules`, param);
  }

  // JS-1936 - per-client Working Time Settings matrix. Columns are the client's own
  // project types (project_types.client_id = clientId); the resolver falls back to the
  // agency Default when the client has no value configured for a rule.
  getClientBusinessRules(clientId: number) {
    return this.http.get<any>(`${environment.apiUrl}/client/${clientId}/business-rules`);
  }

  saveClientBusinessRules(clientId: number, param) {
    return this.http.post<any>(`${environment.apiUrl}/client/${clientId}/business-rules`, param);
  }

  // JS-1920 - per-client "Manage Member Roles" matrix. GET returns the current permissions
  // for the 5 client roles across every functionality that has any client-role row for the
  // owning agency, plus a `locked` list of (functionality, role) cells the UI must disable
  // (e.g. client admin x Manage Member Roles is always-on).
  getClientMemberRoles(clientId: number) {
    return this.http.get<any>(`${environment.apiUrl}/client/${clientId}/member-roles`);
  }

  saveClientMemberRoles(clientId: number, payload) {
    return this.http.post<any>(`${environment.apiUrl}/client/${clientId}/member-roles`, payload);
  }

  // JS-1920 (2026-11-04): per-client Self Service toggle. Flipping rewrites the SS and
  // Orders sections of the matrix with the matching defaults; the Other section stays put.
  toggleClientSelfService(clientId: number, enabled: boolean) {
    return this.http.post<any>(
      `${environment.apiUrl}/client/${clientId}/toggle-self-service`,
      { enabled },
    );
  }

  doesAgencyFallIntoSourceTaxRules(param) {
    return this.http.post<boolean>(`${environment.apiUrl}/agency/does-agency-fall-into-source-tax-rules`, param)
  }

  getOthersPermissions() {
    return this.http.get<any>(`${environment.apiUrl}/agency/others-permissions`);
  }

  getPermission(param) {
    return this.http.post<boolean>(`${environment.apiUrl}/agency/has-permission`, param);
  }
}
